import * as THREE from 'three'
import { forwardRef, useMemo } from 'react'
import RocketShip from './RocketShip';
import { Billboard, Float } from '@react-three/drei'
import { Model as PlanetModel } from '../models/planet';

// Atmosphere shader material
const AtmosphereMaterial = ({ color = "#4FC3F7", opacity = 1, scale = 1.2 }) => {
  const material = useMemo(() => {
    return new THREE.ShaderMaterial({
      vertexShader: `
        varying vec3 vNormal;
        varying vec3 vPositionNormal;
        void main() {
          vNormal = normalize(normalMatrix * normal);
          vPositionNormal = normalize((modelViewMatrix * vec4(position, 1.0)).xyz);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        varying vec3 vNormal;
        varying vec3 vPositionNormal;
        uniform vec3 color;
        uniform float opacity;
        void main() {
          float intensity = pow(abs(dot(vNormal, vPositionNormal)) + 0.4, 2.0);
          // Clamp intensity to prevent bloom feedback
          intensity = clamp(intensity, 0.0, 0.8);
          vec3 finalColor = color * intensity;
          float finalOpacity = opacity * intensity * 0.3; // Further reduced opacity

          gl_FragColor = vec4(finalColor, finalOpacity);
        }
      `,
      uniforms: {
        color: { value: new THREE.Color(color) },
        opacity: { value: 2 }
      },
      blending: THREE.AdditiveBlending,
      side: THREE.BackSide,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      toneMapped: true
    });
  }, [color, opacity]);

  return <primitive object={material} attach="material" />;
};

// Surface atmosphere shader material (sits on planet surface)
const SurfaceAtmosphereMaterial = ({ color = "#4FC3F7", opacity = 1 }) => {
  const material = useMemo(() => {
    return new THREE.ShaderMaterial({
      vertexShader: `
        varying vec3 vNormal;
        varying vec3 vPositionNormal;
        void main() {
          vNormal = normalize(normalMatrix * normal);
          vPositionNormal = normalize((modelViewMatrix * vec4(position, 1.0)).xyz);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        varying vec3 vNormal;
        varying vec3 vPositionNormal;
        uniform vec3 color;
        uniform float opacity;
        void main() {
          // Create edge-only glow for surface atmosphere
          float intensity = 1.0 - abs(dot(vNormal, vPositionNormal));
          intensity = pow(intensity, 3.0); // Sharp edge falloff
          // Clamp intensity to prevent bloom feedback
          intensity = clamp(intensity, 0.0, 0.6);
          vec3 finalColor = color;
          float finalOpacity = opacity * intensity * 0.2; // Further reduced opacity

          gl_FragColor = vec4(finalColor, finalOpacity);
        }
      `,
      uniforms: {
        color: { value: new THREE.Color(color) },
        opacity: { value: 1 }
      },
      blending: THREE.AdditiveBlending,
      side: THREE.FrontSide,
      transparent: true,
      toneMapped: true,
      depthWrite: false,
      depthTest: true
    });
  }, [color, opacity]);

  return <primitive object={material} attach="material" />;
};

// Atmosphere component
const PlanetAtmosphere = ({ scale = [55, 55, 55], color = "#4FC3F7", opacity = 0.5 }) => {
  const atmosphereScale = scale.map(s => s * 1.05); // 5% larger than planet
  
  return (
    <mesh scale={atmosphereScale}>
      <sphereGeometry args={[0.183, 32, 32]} />
      <AtmosphereMaterial color={color} opacity={opacity} />
    </mesh>
  );
};

// Surface atmosphere component (sits on planet surface)
const PlanetSurfaceAtmosphere = ({ scale = [55, 55, 55], color = "#4FC3F7", opacity = 1 }) => {
  return (
    <mesh scale={scale}>
      <sphereGeometry args={[0.184, 32, 32]} />
      <SurfaceAtmosphereMaterial color={color} opacity={opacity} />
    </mesh>
  );
};
const Cake = forwardRef(({ position = [0, 0, -20], showStuckRocket = false }, ref) => {
  return (
    <Billboard follow={true} lockX={false} lockY={false} lockZ={false}>

      <Float
        position={position}
        speed={4} // Animation speed (increased)
        rotationIntensity={2.5} // XYZ rotation intensity (increased for further rotation)
        floatIntensity={0} // No up/down float
        floatingRange={[0, 0]} // No floating range
        ref={ref} // Forward the ref to the Float component
      >
        <group rotation={[Math.PI / 2, 0, 0]}>
          {/* Attach the rocket to the cake when showStuckRocket is true */}
          {showStuckRocket && (
            <group rotation={[-Math.PI / 2, -Math.PI / 8, -Math.PI / 8]}>
              <RocketShip
                position={[0, 0, 1]}
                firing={false}
                floatRotationIntensity={0}
                paused={true}
                gameComplete={true}
                showCrosshair={false}
                showLine={false}
                cakeRef={null}
                resetCounter={0}
              />
            </group>
          )}


  {/* Show planet with atmosphere - with different effects if hit */}
  {showStuckRocket ? (
    <group>
      {/* Normal planet */}
      <PlanetModel scale={[8.25, 8.25, 8.25]} rotation={[-Math.PI * 0.2, Math.PI * 0.2, Math.PI * 0.1]} />
      {/* Surface atmosphere (sits on planet surface) */}
      <PlanetSurfaceAtmosphere scale={[8.26, 8.26, 8.26]} color="#87CEEB" opacity={0.1} />
      {/* Outer atmosphere (blue/cyan glow) */}
      <PlanetAtmosphere scale={[8.25, 8.25, 8.25]} color="#4FC3F7" opacity={0.3} />

    </group>
  ) : (
    <group>
      {/* Normal planet */}
      <PlanetModel scale={[8.25, 8.25, 8.25]} rotation={[-Math.PI * 0.2, Math.PI * 0.2, Math.PI * 0.1]} />
      {/* Surface atmosphere (sits on planet surface) */}
      <PlanetSurfaceAtmosphere scale={[8.26, 8.26, 8.26]} color="#87CEEB" opacity={0.1} />
      {/* Outer atmosphere (blue/cyan glow) */}
      <PlanetAtmosphere scale={[8.25, 8.25, 8.25]} color="#4FC3F7" opacity={0.3} />
    </group>
  )}

      
        </group>
      </Float>
    </Billboard>
  )
})

export default Cake

